'use client';

import { FileWarning, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { clientLogger } from '@/components/workspace-assistant/workspace-assistant-logger';
import { ensureDocxWasmSource } from '@/lib/docx-viewer-wasm';

interface DocxPreviewProps {
  /** Same-origin URL that streams the raw .docx bytes. */
  url: string;
  fileName: string;
}

/**
 * Renders a .docx document with the Extend viewer (Rust/WASM parser with
 * its own pagination engine), which holds numbering, lists, and page
 * geometry faithfully on long documents. Only OOXML (.docx) is supported;
 * legacy binary .doc cannot be parsed here.
 */
export function DocxPreview({ url, fileName }: DocxPreviewProps) {
  const [buffer, setBuffer] = useState<ArrayBuffer | null>(null);
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setBuffer(null);
    setHasFailed(false);
    ensureDocxWasmSource();

    async function load() {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch document (${response.status})`);
        }
        const arrayBuffer = await response.arrayBuffer();
        if (cancelled) return;
        setBuffer(arrayBuffer);
      } catch (error) {
        if (cancelled) return;
        clientLogger.error({ error, url, fileName }, 'Failed to load DOCX preview');
        setHasFailed(true);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [url, fileName]);

  if (hasFailed) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
        <FileWarning className="h-10 w-10 text-neutral-300" />
        <p className="max-w-md text-sm text-neutral-600">
          Gagal menampilkan pratinjau Word ini. Unduh untuk membukanya di aplikasi aslinya.
        </p>
      </div>
    );
  }

  if (!buffer) {
    return (
      <div className="relative h-full w-full overflow-auto bg-neutral-100">
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-neutral-100">
          <Loader2 className="h-7 w-7 animate-spin text-neutral-400" />
          <p className="text-sm font-medium text-neutral-600">Memuat dokumen Word…</p>
        </div>
      </div>
    );
  }

  return <ExtendDocxView buffer={buffer} fileName={fileName} />;
}

function ExtendDocxView({ buffer }: { buffer: ArrayBuffer; fileName: string }): React.ReactElement {
  const [Viewer, setViewer] = useState<React.ComponentType<{ file?: ArrayBuffer }> | null>(null);

  // Loaded on demand so the viewer (and its WASM engine) stays out of the
  // main bundle; the page pays the cost only when a .docx is previewed.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const mod = await import('@extend-ai/react-docx');
      if (!cancelled) setViewer(() => mod.ReactDocxViewer as React.ComponentType<{ file?: ArrayBuffer }>);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!Viewer) {
    return (
      <div className="relative h-full w-full overflow-auto bg-neutral-100">
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-neutral-100">
          <Loader2 className="h-7 w-7 animate-spin text-neutral-400" />
          <p className="text-sm font-medium text-neutral-600">Menyiapkan penampil Word…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-auto bg-neutral-100">
      <Viewer file={buffer} />
    </div>
  );
}
